import React, { useState, useEffect } from 'react';
import { analyticsService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import DisabledFeature from '../components/common/DisabledFeature';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  DollarSign, 
  Truck, 
  MapPin,
  AlertTriangle,
  RefreshCw,
  Download,
  BarChart3
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [topLanes, setTopLanes] = useState([]);
  const [driverProfitability, setDriverProfitability] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.lane_analytics_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadAnalyticsData();
      }
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      const [summaryData, lanesData, profitData] = await Promise.all([
        analyticsService.getSummary(periodDays).catch(() => null),
        analyticsService.getTopLanes(10, 'revenue', periodDays).catch(() => ({ top_lanes: [] })),
        analyticsService.getDriverProfitability(periodDays).catch(() => ({ reports: [] }))
      ]);
      
      setSummary(summaryData);
      setTopLanes(lanesData.top_lanes || []);
      setDriverProfitability(profitData.reports || []);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  };

  const exportDriverProfitability = async () => {
    try {
      const blob = await analyticsService.exportDriverProfitability(periodDays);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `driver_profitability_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      alert('Failed to export report');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <DisabledFeature name="Analytics Dashboard" requiresAuthorization note="Revenue and profitability analytics are built but currently disabled. Configuration is preserved; no operational actions are available." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-gray-600 mt-1">Revenue and profitability insights</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="border rounded-md p-2"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button onClick={loadAnalyticsData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">${(summary.total_revenue || 0).toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Total Revenue</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Truck className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{summary.total_loads || 0}</div>
                    <div className="text-sm text-gray-500">Total Loads</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-8 h-8 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold">{(summary.total_miles || 0).toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Total Miles</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold">${summary.average_revenue_per_mile?.toFixed(2) || '0.00'}</div>
                    <div className="text-sm text-gray-500">Avg $/Mile</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b mb-6 overflow-x-auto">
          {['overview', 'lanes', 'drivers'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'lanes' ? 'Lane Analytics' : tab === 'drivers' ? 'Driver Profitability' : 'Overview'}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Top Lanes by Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                {topLanes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No lane data available</div>
                ) : (
                  <div className="space-y-3">
                    {topLanes.slice(0, 5).map((lane, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                        <div>
                          <div className="font-medium">{lane.origin_state} → {lane.destination_state}</div>
                          <div className="text-xs text-gray-500">{lane.total_loads} loads</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-green-600">${lane.total_revenue?.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">${lane.average_rate_per_mile?.toFixed(2)}/mi</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Top Drivers by Profit</CardTitle>
              </CardHeader>
              <CardContent>
                {driverProfitability.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No driver data available</div>
                ) : (
                  <div className="space-y-3">
                    {driverProfitability.slice(0, 5).map((driver, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                        <div>
                          <div className="font-medium">{driver.driver_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{driver.total_loads} loads</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-green-600">${driver.gross_profit?.toLocaleString()}</div>
                          <Badge className={driver.profit_margin_percent >= 20 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {driver.profit_margin_percent?.toFixed(1)}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'lanes' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Lane Margin Analytics</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topLanes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No lane data available</p>
                </div>
              ) : (
                <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Lane</th>
                        <th className="text-left py-3 px-2">Loads</th>
                        <th className="text-left py-3 px-2">Revenue</th>
                        <th className="text-left py-3 px-2">Miles</th>
                        <th className="text-left py-3 px-2">Avg $/Mile</th>
                        <th className="text-left py-3 px-2">Est. Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topLanes.map((lane, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 font-medium">{lane.origin_state} → {lane.destination_state}</td>
                          <td className="py-3 px-2">{lane.total_loads}</td>
                          <td className="py-3 px-2 text-green-600">${lane.total_revenue?.toLocaleString()}</td>
                          <td className="py-3 px-2">{lane.total_miles?.toLocaleString()}</td>
                          <td className="py-3 px-2">${lane.average_rate_per_mile?.toFixed(2)}</td>
                          <td className="py-3 px-2">
                            <Badge className={lane.estimated_margin_percent >= 20 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {lane.estimated_margin_percent?.toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3" data-testid="lanes-cards">
                  {topLanes.map((lane, idx) => (
                    <div key={idx} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium break-words">{lane.origin_state} → {lane.destination_state}</div>
                        <Badge className={lane.estimated_margin_percent >= 20 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {lane.estimated_margin_percent?.toFixed(1)}%
                        </Badge>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div><span className="text-gray-400">Loads:</span> {lane.total_loads}</div>
                        <div><span className="text-gray-400">Revenue:</span> <span className="text-green-600">${lane.total_revenue?.toLocaleString()}</span></div>
                        <div><span className="text-gray-400">Miles:</span> {lane.total_miles?.toLocaleString()}</div>
                        <div><span className="text-gray-400">$/Mile:</span> ${lane.average_rate_per_mile?.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'drivers' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Driver Profitability</span>
                <Button variant="outline" size="sm" onClick={exportDriverProfitability}>
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {driverProfitability.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No driver profitability data available</p>
                </div>
              ) : (
                <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Driver</th>
                        <th className="text-left py-3 px-2">Loads</th>
                        <th className="text-left py-3 px-2">Revenue</th>
                        <th className="text-left py-3 px-2">Miles</th>
                        <th className="text-left py-3 px-2">Profit</th>
                        <th className="text-left py-3 px-2">Margin</th>
                        <th className="text-left py-3 px-2">$/Mile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverProfitability.map((driver, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="font-medium">{driver.driver_name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{driver.driver_email}</div>
                          </td>
                          <td className="py-3 px-2">{driver.total_loads}</td>
                          <td className="py-3 px-2">${driver.total_revenue?.toLocaleString()}</td>
                          <td className="py-3 px-2">{driver.total_miles?.toLocaleString()}</td>
                          <td className="py-3 px-2 text-green-600">${driver.gross_profit?.toLocaleString()}</td>
                          <td className="py-3 px-2">
                            <Badge className={driver.profit_margin_percent >= 20 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {driver.profit_margin_percent?.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="py-3 px-2">${driver.revenue_per_mile?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3" data-testid="drivers-cards">
                  {driverProfitability.map((driver, idx) => (
                    <div key={idx} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium break-words">{driver.driver_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500 break-all">{driver.driver_email}</div>
                        </div>
                        <Badge className={driver.profit_margin_percent >= 20 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {driver.profit_margin_percent?.toFixed(1)}%
                        </Badge>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div><span className="text-gray-400">Loads:</span> {driver.total_loads}</div>
                        <div><span className="text-gray-400">Revenue:</span> ${driver.total_revenue?.toLocaleString()}</div>
                        <div><span className="text-gray-400">Miles:</span> {driver.total_miles?.toLocaleString()}</div>
                        <div><span className="text-gray-400">Profit:</span> <span className="text-green-600">${driver.gross_profit?.toLocaleString()}</span></div>
                        <div><span className="text-gray-400">$/Mile:</span> ${driver.revenue_per_mile?.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsDashboard;
